import { useEffect, useState, type FormEvent } from 'react';
import { createStaff } from '@/lib/staff';
import {
  EMPLOYMENT_TYPES,
  JOB_TYPES,
  type EmploymentType,
  type JobType,
} from '@/lib/types';

export interface AddStaffModalProps {
  open: boolean;
  primaryLocationId: string;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}

/**
 * Lightweight quick-add modal triggered from the sidebar "+ Add" button.
 * Captures only the fields needed to drag the staff onto the calendar:
 * name + role + employment type. Primary location pre-set to the calendar's
 * current location. HR-style detail fields (address, emergency contacts, etc.)
 * are filled later via the staff detail page.
 */
export function AddStaffModal(props: AddStaffModalProps) {
  const { open, primaryLocationId, onClose, onCreated } = props;

  const [displayName, setDisplayName] = useState('');
  const [jobType, setJobType] = useState<JobType>('pharmacy_assistant');
  const [employmentType, setEmploymentType] = useState<EmploymentType>('part_time');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDisplayName('');
    setJobType('pharmacy_assistant');
    setEmploymentType('part_time');
    setError(null);
  }, [open]);

  if (!open) return null;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await createStaff({
        display_name: displayName.trim(),
        job_type: jobType,
        employment_type: employmentType,
        primary_location_id: primaryLocationId,
        location_ids: [primaryLocationId],
      });
      await onCreated();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-lg">
        <header className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-base font-medium">Add staff</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">×</button>
        </header>

        <form onSubmit={onSubmit} className="p-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            Quick-add for scheduling. You can fill in the rest (contact info, address, emergency contact) later from the staff detail page.
          </p>

          <div className="space-y-1">
            <label htmlFor="display_name" className="block text-xs font-medium">Full name</label>
            <input
              id="display_name"
              type="text"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label htmlFor="job_type" className="block text-xs font-medium">Role</label>
              <select id="job_type" required value={jobType} onChange={(e) => setJobType(e.target.value as JobType)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                {JOB_TYPES.map((j) => (<option key={j.value} value={j.value}>{j.label}</option>))}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="employment_type" className="block text-xs font-medium">Employment</label>
              <select id="employment_type" required value={employmentType} onChange={(e) => setEmploymentType(e.target.value as EmploymentType)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                {EMPLOYMENT_TYPES.map((e) => (<option key={e.value} value={e.value}>{e.label}</option>))}
              </select>
            </div>
          </div>

          {error && <p className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">{error}</p>}

          <footer className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40">Close</button>
            <button type="submit" disabled={submitting} className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50">{submitting ? 'Adding…' : 'Add staff'}</button>
          </footer>
        </form>
      </div>
    </div>
  );
}
